/**
 * Lógica pura da tela `paginas/Dashboard.tsx`.
 *
 * Vive fora do `.tsx` por um motivo prático: o Node 24 remove os tipos de um
 * `.ts` e roda o arquivo direto, mas NÃO compila JSX. Só assim o cálculo de "o
 * que mudou", a leitura da query string e os textos de aviso entram em
 * `node --test` sem navegador nenhum.
 */

import type { Divisao, Painel } from './api'

/**
 * Id da fonte que o gerador de demonstração estampa no snapshot
 * (`servidor/demonstracao.js`). É o que permite reconhecer dado fictício.
 */
export const FONTE_DEMONSTRACAO = 'demonstracao'

/**
 * O painel está mostrando número fictício?
 *
 * `demonstracao: true` **obriga** aviso visível (CONTRATOS, seção 5): o risco de
 * alguém fotografar o telão e tratar R$ 83 mi como venda real é o motivo.
 *
 * Duas leituras, porque uma só não basta hoje: o campo `demonstracao` está no
 * contrato mas `servidor/rotas/painel.js` ainda NÃO o devolve, e o delta do SSE
 * também não o carrega — confiar só nele deixa o aviso apagado justamente no
 * cenário em que ele é obrigatório. A fonte `demonstracao` chega de fato, e sai
 * sozinha quando o primeiro snapshot do coletor substitui a lista de fontes.
 */
export function emDemonstracao(painel: Painel | null): boolean {
  if (painel === null) return false
  if (painel.demonstracao === true) return true
  return Array.isArray(painel.fontes) && painel.fontes.some(f => f?.id === FONTE_DEMONSTRACAO)
}

/* Os tempos da rotação vêm do cadastro (`rotacao.geral_seg` e companhia), que é
   `tipo: 'inteiro'` sem mínimo — nada no caminho impede um `0` salvo no admin.
   Com `segGeral: 0` a tela troca de visão a cada segundo, pelos 4 dias. */
export const SEG_ROTACAO_MINIMO = 3
const SEG_GERAL_PADRAO = 120
const SEG_MARCA_PADRAO = 30
const SEG_PREFETCH_PADRAO = 30

function segundosOuPadrao(valor: unknown, padrao: number): number {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) return padrao
  const inteiro = Math.floor(valor)
  return inteiro < SEG_ROTACAO_MINIMO ? padrao : inteiro
}

/**
 * Os tempos da rotação já peneirados, com os mesmos padrões do cadastro
 * (120 / 30 / 30). Valor inútil cai no padrão em vez de parar o painel.
 *
 * `segPrefetch` é limitado a `segGeral - 1`: o pré-carregamento dispara quando
 * FALTAM tantos segundos, e acima do tempo da visão geral esse instante nunca
 * chega — a marca entraria na tela sem lista pronta, sem erro nenhum à vista.
 */
export function temposDeRotacao(
  rotacao: Partial<Painel['rotacao']> | null | undefined,
): Painel['rotacao'] {
  const segGeral = segundosOuPadrao(rotacao?.segGeral, SEG_GERAL_PADRAO)
  const segMarca = segundosOuPadrao(rotacao?.segMarca, SEG_MARCA_PADRAO)
  const pedido = segundosOuPadrao(rotacao?.segPrefetch, SEG_PREFETCH_PADRAO)
  return { segGeral, segMarca, segPrefetch: Math.min(pedido, segGeral - 1) }
}

/**
 * Sem snapshot há 3 minutos, o painel assume que a fonte caiu e diz isso na
 * tela. É o mesmo padrão do servidor (`WR_IDADE_ALERTA_SEG`, em
 * `painel/servidor/config.js`) — os dois têm de contar a mesma história.
 */
/* 600 s, e nao 180: o limite tem de ser MAIOR que a cadencia da coleta, senao
   ele acusa atraso entre dois ciclos normais. Com a CNF a cada 300 s, o valor
   antigo acendia o alerta em todo ciclo — foi o que tirou o aviso do telao em
   13/10. 600 s cobre dois ciclos e uma folga: a essa altura a fonte parou
   mesmo. */
export const IDADE_ALERTA_PADRAO = 600

/**
 * O conjunto vazio, uma instância só. Devolver sempre a MESMA deixa o React
 * descartar a repintura quando nada mudou (ele compara por identidade).
 * Não mutar: é compartilhado.
 */
export const SEM_MUDANCA: Set<string> = new Set()

/** O mínimo de uma divisão para comparar valores — facilita o teste. */
type ValorDeDivisao = Pick<Divisao, 'id' | 'vendido'>

/**
 * Quais divisões tiveram o valor alterado, e o registro novo dos valores.
 *
 * Devolve um Map NOVO em vez de mexer no recebido: assim o registro tem sempre
 * o tamanho das divisões do momento e não acumula divisão descadastrada pelos
 * 4 dias de evento.
 *
 * `undefined` no registro é "primeira vez que vejo esta divisão" e NÃO conta
 * como mudança — senão a tela abriria com as 8 piscando. Já sair de `null`
 * para um número conta: alguém mediu o que não estava medido.
 */
export function compararVendidos(
  anteriores: ReadonlyMap<string, number | null>,
  divisoes: readonly ValorDeDivisao[],
): { mudaram: Set<string>; valores: Map<string, number | null> } {
  const mudaram = new Set<string>()
  const valores = new Map<string, number | null>()

  for (const divisao of divisoes) {
    const antes = anteriores.get(divisao.id)
    if (antes !== undefined && antes !== divisao.vendido) mudaram.add(divisao.id)
    valores.set(divisao.id, divisao.vendido)
  }

  return { mudaram: mudaram.size === 0 ? SEM_MUDANCA : mudaram, valores }
}

/**
 * O limite de idade do snapshot que acende o aviso de fonte degradada.
 *
 * O campo ainda não existe no contrato: o servidor tem `idadeAlertaSeg` na
 * config e não o publica em `GET /api/painel`. A leitura é defensiva de
 * propósito — no dia em que ele vier (na raiz ou dentro de `rotacao`), a tela
 * passa a respeitá-lo sem alteração nenhuma aqui.
 */
export function limiteIdadeAlerta(painel: Painel | null): number {
  const bruto = painel as
    | (Painel & { idadeAlertaSeg?: unknown; rotacao: { idadeAlertaSeg?: unknown } })
    | null

  for (const candidato of [bruto?.rotacao?.idadeAlertaSeg, bruto?.idadeAlertaSeg]) {
    if (typeof candidato === 'number' && Number.isFinite(candidato) && candidato > 0) {
      return candidato
    }
  }
  return IDADE_ALERTA_PADRAO
}

/** O texto do aviso. Diz a idade e deixa claro que o número ABAIXO não é zero. */
export function textoFonteDegradada(idadeSeg: number): string {
  const segundos = Number.isFinite(idadeSeg) ? Math.max(Math.round(idadeSeg), 0) : 0
  return `Fonte sem resposta há ${segundos} s — os valores abaixo são da última coleta bem-sucedida.`
}

/**
 * O nome que vai na pílula "a seguir X". `usarRotacao` devolve o id da próxima
 * divisão, `'geral'`, ou `null` quando não há divisão cadastrada.
 * Id sem divisão correspondente cai no próprio id em vez de virar vazio — a
 * pílula ficaria sem sentido ("A seguir  · 12s").
 */
export function nomeDaProximaVisao(
  seguinte: string | null,
  divisoes: readonly Pick<Divisao, 'id' | 'nome'>[],
): string {
  if (seguinte === null || seguinte === 'geral') return 'Visão geral'
  return divisoes.find(d => d.id === seguinte)?.nome ?? seguinte
}

/**
 * Os dois parâmetros de conferência da tela:
 *   `?rapido=1`     encurta a rotação (6 s / 3 s) para conferir sem esperar
 *   `?visao=<id>`   abre direto numa divisão
 *
 * `?rapido` sozinho também liga, e `?rapido=0` desliga: quem digita o endereço
 * no telão da sala não vai lembrar da forma exata.
 */
export function lerParametros(busca: string): { rapido: boolean; visao: string | null } {
  const parametros = new URLSearchParams(busca)
  const rapido = parametros.has('rapido') && parametros.get('rapido') !== '0'
  const visao = parametros.get('visao')
  return { rapido, visao: visao === null || visao === '' ? null : visao }
}
